import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, Dialog, DialogBody, DialogHeader, Icon, Skeleton } from '@repo/ui';
import { get } from 'lodash';
import { numberUtil } from '@xtnd/web/shared';
import { Appeal } from '../../types';
import { useGetInvoice, useWriteOffItems } from '../../queries';
import { AppealSummary } from './AppealSummary';
import { WriteOffRemainingForm } from '../common/write-off-remaining/WriteOffRemainingForm';

const AMOUNT_REGEX = /^\d*\.?\d{0,2}$/;

interface MarkWriteOffRemainingModalProps {
  isOpen: boolean;
  isLoading?: boolean;
  appeal: Appeal | null;
  onClose: () => void;
  onSubmit: (feesCut: number, expensesCut: number, comment: string, appealId: string) => Promise<void>;
}

export const MarkWriteOffRemainingModal: React.FC<MarkWriteOffRemainingModalProps> = ({
  isOpen,
  isLoading = false,
  appeal,
  onClose,
  onSubmit,
}) => {
  const [feesCut, setFeesCut] = useState('0');
  const [expensesCut, setExpensesCut] = useState('0');
  const [comment, setComment] = useState('');
  const [errors, setErrors] = useState<Partial<Record<'feesCut' | 'expensesCut', string>>>({});
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const { data: invoice, isLoading: isInvoiceLoading } = useGetInvoice(appeal?.invoiceId ?? '', {
    enabled: isOpen && !!appeal?.invoiceId,
  });

  const outstandingBalance = get(invoice, 'outstandingBalance', 0) as number;
  const currentAppealId = appeal?.appealId ?? (get(invoice, 'appeal.appealId') as string | undefined);

  const { data: writeOffItemsData, isLoading: isWriteOffItemsLoading } = useWriteOffItems(appeal?.invoiceId ?? '');

  const blockMessage = useMemo(() => {
    if (outstandingBalance <= 0) {
      return 'The Outstanding Balance for this invoice is $0.00 or less. There is nothing left to write off.';
    }
    if (invoice && !currentAppealId) {
      return 'Cannot write off: no appeal record is associated with this invoice.';
    }
    if (!writeOffItemsData || !currentAppealId) return null;
    const items = writeOffItemsData.data ?? [];
    const writeOffRemainingItems = items.filter(
      item => item.isWriteOffRemaining && item.appealId === currentAppealId
    );
    if (writeOffRemainingItems.length >= 1) {
      return 'A Write-Off Remaining Amount has already been submitted for this appeal. Please review the existing write-off before proceeding.';
    }
    return null;
  }, [outstandingBalance, writeOffItemsData, currentAppealId, invoice]);

  useEffect(() => {
    if (invoice) {
      const feesCut = get(invoice, 'outstandingFees', 0) as number;
      const expensesCut = get(invoice, 'outstandingExpenses', 0) as number;
      setFeesCut(String(feesCut));
      setExpensesCut(String(expensesCut));
    }
  }, [invoice]);

  // Reset when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFeesCut('0');
      setExpensesCut('0');
      setComment('');
      setErrors({});
      setHasSubmitted(false);
    }
  }, [isOpen]);

  const handleNumberChange = useCallback((field: 'feesCut' | 'expensesCut', value: string) => {
    const normalized = numberUtil.normalizeNumericString(value);

    if (!AMOUNT_REGEX.test(normalized)) return;

    const normalizedValue = normalized || '0';

    if (field === 'feesCut') setFeesCut(normalizedValue);
    else setExpensesCut(normalizedValue);

    setErrors(prev => ({ ...prev, [field]: '' }));

    if (!normalized) return;

    const numericValue = Number(normalized);
    if (numericValue < 0) {
      const label = field === 'feesCut' ? 'Fees Cut' : 'Expenses Cut';
      setErrors(prev => ({ ...prev, [field]: `${label} must be 0 or greater` }));
    }
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!currentAppealId) return;

    const fees = parseFloat(feesCut) || 0;
    const expenses = parseFloat(expensesCut) || 0;
    const total = Math.round((fees + expenses) * 100) / 100;
    const expectedTotal = Math.round(outstandingBalance * 100) / 100;

    if (total !== expectedTotal) {
      setErrors(prev => ({
        ...prev,
        feesCut: `Fees Cut + Expenses Cut must equal the Outstanding Balance (${numberUtil.formatCurrency(outstandingBalance)})`,
      }));
      return;
    }

    setHasSubmitted(true);
    try {
      await onSubmit(fees, expenses, comment, currentAppealId ?? '');
    } finally {
      setHasSubmitted(false);
    }
  }, [feesCut, expensesCut, comment, outstandingBalance, currentAppealId, onSubmit]);

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      size="xxllg"
      showCloseButton={false}
      className="!max-h-[90vh] overflow-y-auto"
    >
      <DialogHeader className="sticky top-0 bg-white dark:bg-xtnd-dark-800 z-[51]">
        <div className="flex justify-between">
          <h3 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-light">
            Write-Off Remaining Amount - INV #{appeal?.invoiceNumber ?? ''}
          </h3>
          <button
            type="button"
            className="absolute z-[52] top-3 right-3 inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-xtnd-blue focus:ring-offset-2 transition-colors dark:text-gray-300 dark:hover:text-gray-100 dark:hover:bg-xtnd-dark-700"
            onClick={onClose}
            aria-label="Close dialog"
          >
            <Icon name="HiOutlineX" />
          </button>
        </div>
      </DialogHeader>

      <DialogBody className="space-y-4">
        {isInvoiceLoading || isWriteOffItemsLoading ? (
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Left: Write-Off Details + Comment */}
            <div className="flex-1 space-y-6">
              {/* Write-Off Details card */}
              <div className="p-6 border border-gray-200 dark:border-gray-700 rounded-xl space-y-4">
                {/* Card header */}
                <div className="flex items-center gap-4 mb-6">
                  <Skeleton variant="rectangular" width={40} height={40} />
                  <Skeleton variant="text" width="40%" height="1.5rem" />
                </div>
                {/* Outstanding Balance */}
                <Skeleton variant="text" width="30%" height="0.75rem" />
                <Skeleton variant="text" width="20%" height="1.5rem" />
                {/* Fees Cut + Expenses Cut inputs */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Skeleton variant="text" width="40%" height="0.75rem" />
                    <Skeleton variant="rectangular" height="2.5rem" />
                  </div>
                  <div className="space-y-1">
                    <Skeleton variant="text" width="40%" height="0.75rem" />
                    <Skeleton variant="rectangular" height="2.5rem" />
                  </div>
                </div>
                {/* Amount to Write Off */}
                <Skeleton variant="text" width="30%" height="0.75rem" />
                <Skeleton variant="text" width="20%" height="1.5rem" />
              </div>

              {/* Comment card */}
              <div className="p-6 border border-gray-200 dark:border-gray-700 rounded-xl space-y-4">
                <div className="flex items-center gap-4 mb-4">
                  <Skeleton variant="rectangular" width={40} height={40} />
                  <Skeleton variant="text" width="25%" height="1.5rem" />
                </div>
                <Skeleton variant="rectangular" height="6rem" />
              </div>
            </div>

            {/* Right: Basic Information + Confirm */}
            <div className="lg:w-80 space-y-6">
              {/* Basic Information card */}
              <div className="p-6 border border-gray-200 dark:border-gray-700 rounded-xl space-y-3">
                <div className="flex items-center gap-2 mb-4">
                  <Skeleton variant="circular" width={20} height={20} />
                  <Skeleton variant="text" width="50%" height="1.25rem" />
                </div>
                {Array.from({ length: 9 }).map((_, i) => (
                  <div key={i} className="flex justify-between">
                    <Skeleton variant="text" width="40%" height="0.875rem" />
                    <Skeleton variant="text" width="35%" height="0.875rem" />
                  </div>
                ))}
              </div>

              {/* Amount to Write Off + Confirm card */}
              <div className="p-6 border border-gray-200 dark:border-gray-700 rounded-xl space-y-4">
                <Skeleton variant="rectangular" height="3rem" />
                <Skeleton variant="rectangular" height="2.5rem" />
              </div>
            </div>
          </div>
        ) : blockMessage && !hasSubmitted ? (
          <div className="flex flex-col lg:flex-row gap-6">
            <div className="flex-1 flex items-center justify-center">
              <Card className="p-8 text-center space-y-3 w-full">
                <div className="flex justify-center">
                  <Icon name="HiExclamationCircle" className="w-10 h-10 text-red-500" />
                </div>
                <h3 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-light">
                  Cannot write-off anymore
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {blockMessage}
                </p>
              </Card>
            </div>
            <div className="lg:w-80">
              <AppealSummary
                basicInformation={{
                  matterDisplayNumber: appeal?.matterDisplayNumber ?? '',
                  clientName: appeal?.clientName ?? '',
                  invoiceDate: appeal?.invoiceDate ?? '',
                  invoiceNumber: appeal?.invoiceNumber ?? '',
                  totalAmount: appeal?.totalAmount ?? 0,
                  amountDue: outstandingBalance,
                  paid: appeal?.paid ?? 0,
                  creditsIssued: appeal?.creditsIssued ?? 0,
                  responsibleAttorneyName: appeal?.responsibleAttorneyName ?? '',
                }}
              />
            </div>
          </div>
        ) : (
          <WriteOffRemainingForm
            invoiceNumber={appeal?.invoiceNumber ?? ''}
            matterDisplayNumber={appeal?.matterDisplayNumber ?? ''}
            clientName={appeal?.clientName ?? ''}
            invoiceDate={appeal?.invoiceDate ?? ''}
            outstandingAmount={outstandingBalance}
            amountPaid={appeal?.paid ?? 0}
            totalAmount={appeal?.totalAmount ?? 0}
            creditsIssued={appeal?.creditsIssued ?? 0}
            responsibleAttorneyName={appeal?.responsibleAttorneyName ?? ''}
            outstandingBalance={outstandingBalance}
            feesCut={feesCut}
            expensesCut={expensesCut}
            comment={comment}
            errors={errors}
            isLoading={isLoading}
            onFeesCutChange={v => handleNumberChange('feesCut', v)}
            onExpensesCutChange={v => handleNumberChange('expensesCut', v)}
            onCommentChange={setComment}
            onSubmit={handleSubmit}
          />
        )}
      </DialogBody>
    </Dialog>
  );
};

export default MarkWriteOffRemainingModal;
